import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Waves, Lock, Menu, X } from "lucide-react";
import { useLang } from "./LangContext";
import { STR } from "../lib/i18n";

export default function Navbar() {
  const { lang, setLang } = useLang();
  const t = STR[lang];
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const links = [
    { to: "/", label: t.nav.home, end: true, id: "home" },
    { to: "/reportage", label: t.nav.reportage, id: "reportage" },
    { to: "/nyheter", label: t.nav.news, id: "news" },
    { to: "/verktyg", label: t.nav.tool, id: "tool" },
  ];

  const linkClass = ({ isActive }) =>
    `overline-label transition-colors duration-300 ${isActive ? "text-[#9E643E]" : "text-[#2D4032] hover:text-[#9E643E]"}`;

  return (
    <header className="fixed top-0 left-0 right-0 z-50 border-b border-[#D8D3C4]/60 bg-[#F6F5F0]/85 backdrop-blur-xl">
      <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 md:px-12">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-2 text-[#2D4032]">
          <Waves className="h-5 w-5" strokeWidth={1.5} />
          <span className="font-serif text-xl font-semibold tracking-tight">Sportfiskevatten</span>
        </Link>

        {/* DESKTOP-MENY */}
        <nav className="hidden items-center gap-6 md:flex md:gap-10">
          {links.map((l) => (
            <NavLink key={l.id} to={l.to} end={l.end} data-testid={`nav-${l.id}-link`} className={linkClass}>
              {l.label}
            </NavLink>
          ))}
          <div className="flex items-center gap-1.5 text-xs font-semibold tracking-widest" data-testid="lang-switcher">
            <button
              onClick={() => setLang("sv")}
              data-testid="lang-sv-button"
              className={`transition-colors duration-300 ${lang === "sv" ? "text-[#9E643E]" : "text-[#7B8A80] hover:text-[#2D4032]"}`}
            >
              SV
            </button>
            <span className="text-[#D8D3C4]">/</span>
            <button
              onClick={() => setLang("en")}
              data-testid="lang-en-button"
              className={`transition-colors duration-300 ${lang === "en" ? "text-[#9E643E]" : "text-[#7B8A80] hover:text-[#2D4032]"}`}
            >
              EN
            </button>
          </div>
          <Link
            to="/redaktion"
            data-testid="nav-admin-link"
            className="flex items-center gap-1.5 text-[#7B8A80] transition-colors duration-300 hover:text-[#9E643E]"
            aria-label="Redaktion"
          >
            <Lock className="h-4 w-4" strokeWidth={1.5} />
            <span className="overline-label">{t.nav.admin}</span>
          </Link>
        </nav>

        {/* MOBIL: språk + hamburgare */}
        <div className="flex items-center gap-4 md:hidden">
          <div className="flex items-center gap-1.5 text-xs font-semibold tracking-widest" data-testid="lang-switcher-mobile">
            <button
              onClick={() => setLang("sv")}
              data-testid="lang-sv-button-mobile"
              className={`transition-colors duration-300 ${lang === "sv" ? "text-[#9E643E]" : "text-[#7B8A80] hover:text-[#2D4032]"}`}
            >
              SV
            </button>
            <span className="text-[#D8D3C4]">/</span>
            <button
              onClick={() => setLang("en")}
              data-testid="lang-en-button-mobile"
              className={`transition-colors duration-300 ${lang === "en" ? "text-[#9E643E]" : "text-[#7B8A80] hover:text-[#2D4032]"}`}
            >
              EN
            </button>
          </div>
          <button
            onClick={() => setOpen((o) => !o)}
            data-testid="nav-mobile-toggle"
            aria-label="Meny"
            aria-expanded={open}
            className="flex h-10 w-10 items-center justify-center text-[#2D4032] transition-colors duration-300 hover:text-[#9E643E]"
          >
            {open ? <X className="h-6 w-6" strokeWidth={1.5} /> : <Menu className="h-6 w-6" strokeWidth={1.5} />}
          </button>
        </div>
      </div>

      {/* MOBILMENY */}
      {open && (
        <nav
          data-testid="nav-mobile-menu"
          className="border-t border-[#D8D3C4]/60 bg-[#F6F5F0]/95 backdrop-blur-xl md:hidden"
        >
          {links.map((l) => (
            <NavLink
              key={l.id}
              to={l.to}
              end={l.end}
              data-testid={`nav-mobile-${l.id}-link`}
              className={({ isActive }) =>
                `overline-label block border-b border-[#D8D3C4]/40 px-6 py-4 transition-colors duration-300 ${
                  isActive ? "text-[#9E643E]" : "text-[#2D4032] hover:text-[#9E643E]"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
          <Link
            to="/redaktion"
            data-testid="nav-mobile-admin-link"
            className="overline-label flex items-center gap-2 px-6 py-4 text-[#7B8A80] transition-colors duration-300 hover:text-[#9E643E]"
          >
            <Lock className="h-4 w-4" strokeWidth={1.5} />
            {t.nav.admin}
          </Link>
        </nav>
      )}
    </header>
  );
}
